import { z } from 'zod';

// =====================
// Schedule Exception Types
// =====================

export enum ScheduleExceptionType {
  PTO = 'PTO',
  SICK = 'SICK',
  HOLIDAY = 'HOLIDAY',
  BLOCKED = 'BLOCKED',
  OVERRIDE = 'OVERRIDE',
  TRAINING = 'TRAINING',
  PERSONAL = 'PERSONAL',
  JURY_DUTY = 'JURY_DUTY',
  BEREAVEMENT = 'BEREAVEMENT',
  PARENTAL = 'PARENTAL',
  UNPAID = 'UNPAID',
}

export enum ScheduleExceptionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

// Human-readable labels for exception types
export const ScheduleExceptionTypeLabels: Record<ScheduleExceptionType, string> = {
  [ScheduleExceptionType.PTO]: 'Paid Time Off',
  [ScheduleExceptionType.SICK]: 'Sick Leave',
  [ScheduleExceptionType.HOLIDAY]: 'Holiday',
  [ScheduleExceptionType.BLOCKED]: 'Blocked Time',
  [ScheduleExceptionType.OVERRIDE]: 'Schedule Override',
  [ScheduleExceptionType.TRAINING]: 'Training',
  [ScheduleExceptionType.PERSONAL]: 'Personal Time',
  [ScheduleExceptionType.JURY_DUTY]: 'Jury Duty',
  [ScheduleExceptionType.BEREAVEMENT]: 'Bereavement',
  [ScheduleExceptionType.PARENTAL]: 'Parental Leave',
  [ScheduleExceptionType.UNPAID]: 'Unpaid Leave',
};

// Colors for calendar display
export const ScheduleExceptionTypeColors: Record<ScheduleExceptionType, string> = {
  [ScheduleExceptionType.PTO]: '#14b8a6', // teal (distinct from Available green)
  [ScheduleExceptionType.SICK]: '#ef4444', // red
  [ScheduleExceptionType.HOLIDAY]: '#8b5cf6', // purple
  [ScheduleExceptionType.BLOCKED]: '#6b7280', // gray
  [ScheduleExceptionType.OVERRIDE]: '#3b82f6', // blue
  [ScheduleExceptionType.TRAINING]: '#f59e0b', // amber
  [ScheduleExceptionType.PERSONAL]: '#06b6d4', // cyan
  [ScheduleExceptionType.JURY_DUTY]: '#64748b', // slate
  [ScheduleExceptionType.BEREAVEMENT]: '#71717a', // zinc
  [ScheduleExceptionType.PARENTAL]: '#ec4899', // pink
  [ScheduleExceptionType.UNPAID]: '#a3a3a3', // neutral
};

// =====================
// Recurring Pattern for Schedule
// =====================

export const ScheduleRecurringPatternSchema = z.object({
  frequency: z.enum(['daily', 'weekly', 'biweekly', 'monthly', 'yearly']),
  daysOfWeek: z.array(z.number().min(0).max(6)).optional(), // 0=Sunday, 6=Saturday
  dayOfMonth: z.number().min(1).max(31).optional(),
  until: z.string().optional(), // ISO date string
  count: z.number().int().positive().optional(), // number of occurrences
});

export type ScheduleRecurringPattern = z.infer<typeof ScheduleRecurringPatternSchema>;

// =====================
// Member Availability
// =====================

export const MemberAvailabilitySchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  dayOfWeek: z.number().int().min(0).max(6), // 0=Sunday, 6=Saturday
  startTime: z.string().regex(/^\d{2}:\d{2}$/), // "09:00" format
  endTime: z.string().regex(/^\d{2}:\d{2}$/), // "17:00" format
  isAvailable: z.boolean().default(true),
  timezone: z.string().default('UTC'),
  effectiveFrom: z.date().optional().nullable(),
  effectiveTo: z.date().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
});

export type MemberAvailability = z.infer<typeof MemberAvailabilitySchema>;

export const CreateMemberAvailabilitySchema = MemberAvailabilitySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
});

export type CreateMemberAvailability = z.infer<typeof CreateMemberAvailabilitySchema>;

export const UpdateMemberAvailabilitySchema = CreateMemberAvailabilitySchema.partial().omit({
  memberId: true,
  organizationId: true,
});

export type UpdateMemberAvailability = z.infer<typeof UpdateMemberAvailabilitySchema>;

// Bulk availability update (set entire week at once)
export const BulkAvailabilitySchema = z.object({
  memberId: z.string().uuid(),
  timezone: z.string().default('UTC'),
  effectiveFrom: z.date().optional().nullable(),
  effectiveTo: z.date().optional().nullable(),
  slots: z.array(
    z.object({
      dayOfWeek: z.number().int().min(0).max(6),
      startTime: z.string().regex(/^\d{2}:\d{2}$/),
      endTime: z.string().regex(/^\d{2}:\d{2}$/),
      isAvailable: z.boolean().default(true),
    })
  ),
});

export type BulkAvailability = z.infer<typeof BulkAvailabilitySchema>;

// =====================
// Schedule Exception
// =====================

export const ScheduleExceptionSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  exceptionType: z.nativeEnum(ScheduleExceptionType),
  startDateTime: z.date(),
  endDateTime: z.date(),
  allDay: z.boolean().default(false),
  title: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(ScheduleExceptionStatus).default(ScheduleExceptionStatus.PENDING),
  approvedBy: z.string().uuid().optional().nullable(),
  approvedAt: z.date().optional().nullable(),
  rejectionReason: z.string().optional().nullable(),
  isRecurring: z.boolean().default(false),
  recurringPattern: ScheduleRecurringPatternSchema.optional().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
  member: z.any().optional(),
  approver: z.any().optional(),
  creator: z.any().optional(),
});

export type ScheduleException = z.infer<typeof ScheduleExceptionSchema>;

export const CreateScheduleExceptionSchema = ScheduleExceptionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  member: true,
  approver: true,
  creator: true,
  approvedAt: true,
  approvedBy: true,
  rejectionReason: true,
  status: true,
});

export type CreateScheduleException = z.infer<typeof CreateScheduleExceptionSchema>;

export const UpdateScheduleExceptionSchema = CreateScheduleExceptionSchema.partial().omit({
  memberId: true,
  organizationId: true,
  createdBy: true,
});

export type UpdateScheduleException = z.infer<typeof UpdateScheduleExceptionSchema>;

// =====================
// Schedule Template
// =====================

// Time slot within a day
export const TimeSlotSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/), // "09:00"
  end: z.string().regex(/^\d{2}:\d{2}$/), // "17:00"
});

export type TimeSlot = z.infer<typeof TimeSlotSchema>;

// Weekly pattern: map of day number to array of time slots
export const WeeklyPatternSchema = z.record(
  z.string(), // "0" through "6"
  z.array(TimeSlotSchema)
);

export type WeeklyPattern = z.infer<typeof WeeklyPatternSchema>;

export const ScheduleTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().optional().nullable(),
  weeklyPattern: WeeklyPatternSchema,
  timezone: z.string().default('UTC'),
  totalHoursPerWeek: z.number(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  creator: z.any().optional(),
});

export type ScheduleTemplate = z.infer<typeof ScheduleTemplateSchema>;

export const CreateScheduleTemplateSchema = ScheduleTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  creator: true,
});

export type CreateScheduleTemplate = z.infer<typeof CreateScheduleTemplateSchema>;

export const UpdateScheduleTemplateSchema = CreateScheduleTemplateSchema.partial().omit({
  organizationId: true,
  createdBy: true,
});

export type UpdateScheduleTemplate = z.infer<typeof UpdateScheduleTemplateSchema>;

// =====================
// Organization Holiday
// =====================

export const OrganizationHolidaySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  date: z.date(),
  isRecurring: z.boolean().default(false),
  departments: z.array(z.string()).default([]),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  creator: z.any().optional(),
});

export type OrganizationHoliday = z.infer<typeof OrganizationHolidaySchema>;

export const CreateOrganizationHolidaySchema = OrganizationHolidaySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  creator: true,
});

export type CreateOrganizationHoliday = z.infer<typeof CreateOrganizationHolidaySchema>;

export const UpdateOrganizationHolidaySchema = CreateOrganizationHolidaySchema.partial().omit({
  organizationId: true,
  createdBy: true,
});

export type UpdateOrganizationHoliday = z.infer<typeof UpdateOrganizationHolidaySchema>;

// =====================
// Team Schedule View Types
// =====================

// A single day's schedule for a member
export const DayScheduleSchema = z.object({
  date: z.string(), // ISO date
  memberId: z.string().uuid(),
  memberName: z.string(),
  memberImage: z.string().optional().nullable(),
  department: z.string().optional().nullable(),

  // Base availability (from MemberAvailability)
  baseSlots: z.array(TimeSlotSchema),

  // Exceptions that affect this day
  exceptions: z.array(
    z.object({
      id: z.string().uuid(),
      type: z.nativeEnum(ScheduleExceptionType),
      title: z.string().optional().nullable(),
      startTime: z.string(), // "09:00" or full datetime for partial day
      endTime: z.string(),
      allDay: z.boolean(),
      status: z.nativeEnum(ScheduleExceptionStatus),
    })
  ),

  // Calculated availability
  availableHours: z.number(),
  isHoliday: z.boolean(),
  holidayName: z.string().optional().nullable(),
});

export type DaySchedule = z.infer<typeof DayScheduleSchema>;

// Team schedule for a date range
export const TeamScheduleSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  members: z.array(
    z.object({
      memberId: z.string().uuid(),
      memberName: z.string(),
      memberImage: z.string().optional().nullable(),
      department: z.string().optional().nullable(),
      jobTitle: z.string().optional().nullable(),
      days: z.array(DayScheduleSchema),
    })
  ),
  holidays: z.array(
    z.object({
      date: z.string(),
      name: z.string(),
    })
  ),
});

export type TeamSchedule = z.infer<typeof TeamScheduleSchema>;

// =====================
// Availability Check
// =====================

export const AvailabilityCheckRequestSchema = z.object({
  memberIds: z.array(z.string().uuid()).optional(), // If empty, check all members
  startDate: z.string(), // ISO date
  endDate: z.string(),
  requiredHours: z.number().optional(), // Minimum hours needed
});

export type AvailabilityCheckRequest = z.infer<typeof AvailabilityCheckRequestSchema>;

export const AvailabilityCheckResultSchema = z.object({
  memberId: z.string().uuid(),
  memberName: z.string(),
  totalAvailableHours: z.number(),
  isAvailable: z.boolean(),
  conflicts: z.array(
    z.object({
      date: z.string(),
      reason: z.string(),
      type: z.nativeEnum(ScheduleExceptionType).optional(),
    })
  ),
});

export type AvailabilityCheckResult = z.infer<typeof AvailabilityCheckResultSchema>;

// =====================
// Time Off Request Summary
// =====================

export const TimeOffSummarySchema = z.object({
  memberId: z.string().uuid(),
  year: z.number(),
  ptoUsed: z.number(), // hours
  ptoRemaining: z.number().optional(), // if org tracks PTO balance
  sickDaysUsed: z.number(),
  otherTimeOff: z.number(),
  pendingRequests: z.number(), // count of pending requests
});

export type TimeOffSummary = z.infer<typeof TimeOffSummarySchema>;

// =====================
// Default Schedule Templates
// =====================

export const DEFAULT_SCHEDULE_TEMPLATES = {
  STANDARD_9_TO_5: {
    name: 'Standard 9-5',
    description: 'Monday to Friday, 9:00 AM to 5:00 PM',
    weeklyPattern: {
      '1': [{ start: '09:00', end: '17:00' }],
      '2': [{ start: '09:00', end: '17:00' }],
      '3': [{ start: '09:00', end: '17:00' }],
      '4': [{ start: '09:00', end: '17:00' }],
      '5': [{ start: '09:00', end: '17:00' }],
    },
    totalHoursPerWeek: 40,
  },
  PART_TIME_MORNINGS: {
    name: 'Part-time Mornings',
    description: 'Monday to Friday, 9:00 AM to 1:00 PM',
    weeklyPattern: {
      '1': [{ start: '09:00', end: '13:00' }],
      '2': [{ start: '09:00', end: '13:00' }],
      '3': [{ start: '09:00', end: '13:00' }],
      '4': [{ start: '09:00', end: '13:00' }],
      '5': [{ start: '09:00', end: '13:00' }],
    },
    totalHoursPerWeek: 20,
  },
  FLEXIBLE_40: {
    name: 'Flexible 40 Hours',
    description: 'Monday to Friday with flexible hours',
    weeklyPattern: {
      '1': [{ start: '08:00', end: '18:00' }],
      '2': [{ start: '08:00', end: '18:00' }],
      '3': [{ start: '08:00', end: '18:00' }],
      '4': [{ start: '08:00', end: '18:00' }],
      '5': [{ start: '08:00', end: '18:00' }],
    },
    totalHoursPerWeek: 40,
  },
  FOUR_DAY_WEEK: {
    name: '4-Day Week',
    description: 'Monday to Thursday, 8:00 AM to 6:00 PM',
    weeklyPattern: {
      '1': [{ start: '08:00', end: '18:00' }],
      '2': [{ start: '08:00', end: '18:00' }],
      '3': [{ start: '08:00', end: '18:00' }],
      '4': [{ start: '08:00', end: '18:00' }],
    },
    totalHoursPerWeek: 40,
  },
} as const;
